/**
 * Normalized device metadata — allow-list-only enums.
 *
 * We deliberately DO NOT keep the raw user-agent string. Instead the
 * chairside code emits only:
 *   device_class    : "mobile" | "tablet" | "desktop"
 *   browser_family  : "safari" | "chrome" | "firefox" | "edge" | "samsung" | "other"
 *   os_family       : "ios" | "android" | "macos" | "windows" | "linux" | "chromeos" | "other"
 *
 * Anything the parser can't confidently pin lands in "other". The backend
 * validator rejects any value outside the allow-list, so a compromised
 * client cannot smuggle free text or version strings through this field.
 */

const OS_FAMILIES = new Set(["ios", "android", "macos", "windows", "linux", "chromeos", "other"]);
const BROWSER_FAMILIES = new Set(["safari", "chrome", "firefox", "edge", "samsung", "other"]);
const DEVICE_CLASSES = new Set(["mobile", "tablet", "desktop"]);

function _deviceClass(ua) {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth || 0;
  if (w <= 767) return "mobile";
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua) && w <= 1024) return "tablet";
  if (w <= 1024) return "tablet";
  return "desktop";
}

function _osFamily(ua) {
  if (/iPhone|iPad|iPod|iOS/.test(ua)) return "ios";
  if (/Mac OS X/.test(ua) && /Safari/.test(ua) && !/iPhone|iPad/.test(ua)) return "macos";
  if (/Android/.test(ua)) return "android";
  if (/CrOS/.test(ua)) return "chromeos";
  if (/Windows/.test(ua)) return "windows";
  if (/Linux/.test(ua)) return "linux";
  return "other";
}

function _browserFamily(ua) {
  // Order matters — Chrome UA also contains "Safari", Edge UA also contains
  // "Chrome". We check the most specific first.
  if (/Edg\//.test(ua)) return "edge";
  if (/SamsungBrowser/.test(ua)) return "samsung";
  if (/Firefox\//.test(ua) || /FxiOS/.test(ua)) return "firefox";
  if (/Chrome\/|CriOS/.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

/**
 * Read normalized device metadata from the current browser. Returns fields
 * that are guaranteed to be inside the backend allow-list.
 */
export function getDeviceMeta() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const dc = _deviceClass(ua);
  const bf = _browserFamily(ua);
  const of = _osFamily(ua);
  return {
    device_class: DEVICE_CLASSES.has(dc) ? dc : "desktop",
    browser_family: BROWSER_FAMILIES.has(bf) ? bf : "other",
    os_family: OS_FAMILIES.has(of) ? of : "other",
  };
}
